import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionLinesEntity } from "entities/ProductionLines/ProductionLinesEntity";
import { Formik, Form, FormikHelpers } from "formik";
import {
    Box,
    Button,
    Checkbox,
    Typography,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    ListItemText,
    Chip,
    FormHelperText,
} from "@mui/material";
import { IClustersCreationFormValues, IClustersCreationForm } from "./ClusterCreationFormInterfaces";
import { addOrUpdateClusterSchema } from "validations/schemas";
import { useStyles } from "./ClustersCreationForm.styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { ClusterEntity } from "entities/ClusterEntity/ClusterEntity";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import useRolesPermissions from "hooks/useRolesPermissions";
import MaderaLoader from "components/MaderaLoader";

export const ClustersCreationForm = ({ handleClose, millId, cluster, clustersRefetch }: IClustersCreationForm) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const {
        loading,
        productionLines,
        refetch: productionLinesRefetch,
    } = ProductionLinesEntity.GetProductionLinesCorporateBO(millId);
    const { addOrUpdateCluster, apolloMutationError } = ClusterEntity.AddOrUpdateCluster();
    const { setHMIsClusterId } = MachineCentersEntity.SetMachineCentersClusterId();
    const { deleteCluster } = ClusterEntity.SoftDeleteCluster();
    const [deleteClusterAction, setDeleteClusterAction] = useState<boolean>(false);
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const filterSelectedProductionLinesIds = useCallback((selected: number[]) => {
        const filteredSelectedProductionLinesIds = selected.filter(
            (selectedId, index, array) => array.indexOf(selectedId) === index,
        );

        return filteredSelectedProductionLinesIds;
    }, []);

    const formattedProductionLines = useMemo(() => {
        return (
            !loading &&
            productionLines.length > 0 &&
            productionLines.map((productionLine) => {
                return {
                    id: productionLine.production_line_id,
                    name: productionLine.name,
                    machine_centers: productionLine.machine_centers.map((machine_center) => {
                        return {
                            id: machine_center.machine_center_id,
                            name: machine_center.name,
                        };
                    }),
                };
            })
        );
    }, [loading, productionLines]);

    const handleSubmit = useCallback(
        async (values: IClustersCreationFormValues, { setSubmitting }: FormikHelpers<IClustersCreationFormValues>) => {
            //editing cluster
            if (cluster?.cluster_id) {
                try {
                    const editedCluster = {
                        cluster_id: cluster.cluster_id,
                        name: values.name,
                        mill_id: millId,
                        corporate_id: productionLines[0].mill.corporate_id,
                    };
                    const addClusterResponse = await addOrUpdateCluster({ variables: { cluster: editedCluster } });

                    const removedMachineCentersFromCluster = cluster.machine_centers
                        .filter((machineCenter) => !values.machine_centers.includes(machineCenter.machine_center_id))
                        .map((machineCenter) => machineCenter.machine_center_id);

                    const addClusterIdInMachineCenters = await setHMIsClusterId({
                        variables: {
                            machineCenterIds: values.machine_centers,
                            changes: {
                                cluster_id: addClusterResponse.data.insert_cluster.returning[0].cluster_id,
                            },
                        },
                    });

                    const removeClusterIdFromMachineCenters = await setHMIsClusterId({
                        variables: {
                            machineCenterIds: removedMachineCentersFromCluster,
                            changes: {
                                cluster_id: null,
                            },
                        },
                    });

                    if (addClusterIdInMachineCenters || removeClusterIdFromMachineCenters) {
                        productionLinesRefetch();
                        handleClose();
                    }
                } catch (error) {
                    console.log("error", error);
                }
            } else {
                //new cluster
                const newCluster = {
                    name: values.name,
                    mill_id: millId,
                    corporate_id: productionLines[0].mill.corporate_id,
                };
                try {
                    const addClusterResponse = await addOrUpdateCluster({ variables: { cluster: newCluster } });

                    const addClusterIdInMachineCenters = await setHMIsClusterId({
                        variables: {
                            machineCenterIds: values.machine_centers,
                            changes: {
                                cluster_id: addClusterResponse.data.insert_cluster.returning[0].cluster_id,
                            },
                        },
                    });

                    if (addClusterIdInMachineCenters) {
                        productionLinesRefetch();
                        handleClose();
                    }
                } catch (error) {
                    console.log("error", error);
                }
            }
        },
        [addOrUpdateCluster, millId, productionLines, setHMIsClusterId, handleClose, cluster, productionLinesRefetch],
    );

    const handleDeleteCluster = useCallback(async () => {
        try {
            const response = await deleteCluster({ variables: { cluster_id: cluster?.cluster_id } });

            if (response) {
                clustersRefetch();
                productionLinesRefetch();
            }
        } catch (error) {
            console.log("error", error);
        }
    }, [cluster?.cluster_id, clustersRefetch, productionLinesRefetch, deleteCluster]);

    const editableClusterProductionLines = useMemo(
        () =>
            cluster &&
            cluster.machine_centers
                .flatMap((machineCenter) => machineCenter.production_line.production_line_id)
                .reduce((uniqueIds: number[], id) => {
                    if (!uniqueIds.includes(id)) {
                        uniqueIds.push(id);
                    }
                    return uniqueIds;
                }, []),
        [cluster],
    );

    let editableClusterMachineCenters = useMemo(
        () => cluster && cluster.machine_centers.flatMap((machineCenter) => machineCenter.machine_center_id),
        [cluster],
    );

    const initFormValues: IClustersCreationFormValues = useMemo(() => {
        return {
            name: cluster?.name ?? "",
            production_lines: editableClusterProductionLines ?? [],
            machine_centers: editableClusterMachineCenters ?? [],
        };
    }, [cluster?.name, editableClusterMachineCenters, editableClusterProductionLines]);

    const handleProductionLineChange = useCallback(
        (targetProductionLines: number[], values: IClustersCreationFormValues) => {
            const selectedProductionLines = productionLines.filter((productionLine) =>
                targetProductionLines.includes(productionLine.production_line_id),
            );

            initFormValues.machine_centers = values.machine_centers.filter((machineCenterId) =>
                selectedProductionLines
                    .flatMap((selectedProductionLine) =>
                        selectedProductionLine.machine_centers.map((machineCenter) => machineCenter.machine_center_id),
                    )
                    .includes(machineCenterId),
            );
        },
        [productionLines, initFormValues],
    );

    return (
        <Formik initialValues={initFormValues} validationSchema={addOrUpdateClusterSchema} onSubmit={handleSubmit}>
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <div className={classes.modalBody}>
                            <Box className={classes.boxContainer}>
                                <InputLabel id="outlined-name-label">
                                    <Typography variant="subtitle2" className={classes.formLabel}>
                                        {t("corporate_backoffice.cluster_view.cluster_creation_form.name")}
                                    </Typography>
                                </InputLabel>
                                <TextField
                                    aria-labelledby="outlined-name-label"
                                    fullWidth
                                    id="outlined-name-input"
                                    className={classes.textField}
                                    name="name"
                                    type="text"
                                    variant="outlined"
                                    value={values.name}
                                    onChange={handleChange}
                                    error={
                                        (touched.name && Boolean(errors.name)) ||
                                        apolloMutationError?.message.includes("uq_cluster_name")
                                    }
                                    helperText={
                                        (touched.name && errors.name && t(errors.name)) ||
                                        (apolloMutationError?.message.includes("uq_cluster_name") &&
                                            t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.cluster_name_already_exists",
                                            ))
                                    }
                                />
                            </Box>
                            <Box className={classes.boxContainer}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="outlined-name-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.select_production_lines",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <>
                                        <Select
                                            name={"production_lines"}
                                            labelId={t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.select_production_lines",
                                            )}
                                            placeholder={t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.select_production_lines",
                                            )}
                                            multiple
                                            IconComponent={KeyboardArrowDownIcon}
                                            style={{ width: "100%" }}
                                            value={values.production_lines || []}
                                            onChange={(e) => {
                                                handleChange(e);
                                                handleProductionLineChange(e.target.value as number[], values);
                                            }}
                                            renderValue={(selected) => {
                                                const filteredSelectedProductionLinesIds =
                                                    filterSelectedProductionLinesIds(selected);
                                                return (
                                                    <Box className={classes.box}>
                                                        {loading ? (
                                                            <MaderaLoader />
                                                        ) : (
                                                            filteredSelectedProductionLinesIds.map((value) => {
                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={
                                                                            !loading &&
                                                                            typeof formattedProductionLines !==
                                                                                "boolean" &&
                                                                            formattedProductionLines.length > 0 &&
                                                                            formattedProductionLines.find(
                                                                                (productionLine) =>
                                                                                    productionLine.id === value,
                                                                            )?.name
                                                                        }
                                                                    />
                                                                );
                                                            })
                                                        )}
                                                    </Box>
                                                );
                                            }}
                                        >
                                            {!loading &&
                                                typeof formattedProductionLines !== "boolean" &&
                                                formattedProductionLines.length > 0 &&
                                                formattedProductionLines.map((option) => (
                                                    <MenuItem
                                                        key={option.id}
                                                        value={option.id}
                                                        className={classes.menuItem}
                                                    >
                                                        <Checkbox
                                                            color="info"
                                                            checked={values.production_lines.includes(option.id)}
                                                        />{" "}
                                                        <ListItemText>{option.name}</ListItemText>
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                        <FormHelperText className={classes.error}>
                                            {touched.production_lines &&
                                                errors.production_lines &&
                                                t(errors.production_lines)}
                                        </FormHelperText>
                                    </>
                                </Box>
                            </Box>
                            <Box className={classes.boxContainer}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="outlined-name-label" className={classes.machineCenterInputLabel}>
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.select_machine_centers",
                                            )}
                                        </Typography>
                                        <Typography variant="h6" className={classes.maxMachineCenterAmountLegend}>
                                            {t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.max_amount_of_machine_centers",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <>
                                        <Select
                                            name={"machine_centers"}
                                            labelId={t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.select_machine_centers",
                                            )}
                                            placeholder={t(
                                                "corporate_backoffice.cluster_view.cluster_creation_form.select_machine_centers",
                                            )}
                                            multiple
                                            IconComponent={KeyboardArrowDownIcon}
                                            style={{ width: "100%" }}
                                            value={values.machine_centers || []}
                                            onChange={handleChange}
                                            renderValue={(selected) => {
                                                return (
                                                    <Box className={classes.box}>
                                                        {loading ? (
                                                            <MaderaLoader />
                                                        ) : (
                                                            selected.map((value) => {
                                                                const filteredProductionLineMachineCenter =
                                                                    productionLines
                                                                        .filter((productionLine) =>
                                                                            values.production_lines.includes(
                                                                                productionLine.production_line_id,
                                                                            ),
                                                                        )
                                                                        .flatMap(
                                                                            (productionLine) =>
                                                                                productionLine.machine_centers,
                                                                        )
                                                                        .find((hmi) => hmi.machine_center_id === value);

                                                                return (
                                                                    filteredProductionLineMachineCenter && (
                                                                        <Chip
                                                                            key={value}
                                                                            label={
                                                                                productionLines
                                                                                    .filter((productionLine) =>
                                                                                        values.production_lines.includes(
                                                                                            productionLine.production_line_id,
                                                                                        ),
                                                                                    )
                                                                                    .flatMap(
                                                                                        (productionLine) =>
                                                                                            productionLine.machine_centers,
                                                                                    )
                                                                                    .find(
                                                                                        (hmi) =>
                                                                                            hmi.machine_center_id ===
                                                                                            value,
                                                                                    )?.name
                                                                            }
                                                                        />
                                                                    )
                                                                );
                                                            })
                                                        )}
                                                    </Box>
                                                );
                                            }}
                                        >
                                            {productionLines
                                                .filter((productionLine) =>
                                                    values.production_lines.includes(productionLine.production_line_id),
                                                )
                                                .flatMap((productionLine) => productionLine.machine_centers)
                                                .map((machineCenter) => {
                                                    return (
                                                        <MenuItem
                                                            key={machineCenter.machine_center_id}
                                                            value={machineCenter.machine_center_id}
                                                            className={classes.menuItem}
                                                            disabled={Boolean(
                                                                (machineCenter.cluster &&
                                                                    machineCenter.cluster.cluster_id !==
                                                                        cluster?.cluster_id) ||
                                                                    (machineCenter.cluster &&
                                                                        machineCenter.cluster.cluster_id &&
                                                                        !cluster),
                                                            )}
                                                        >
                                                            <>
                                                                <Checkbox
                                                                    color="info"
                                                                    checked={values.machine_centers.includes(
                                                                        machineCenter.machine_center_id,
                                                                    )}
                                                                />{" "}
                                                                <ListItemText>{machineCenter.name}</ListItemText>
                                                                {machineCenter.cluster &&
                                                                    machineCenter.cluster.cluster_id !==
                                                                        cluster?.cluster_id && (
                                                                        <Chip
                                                                            className={classes.clusterChip}
                                                                            label={machineCenter.cluster.name}
                                                                        />
                                                                    )}
                                                            </>
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>

                                        <FormHelperText className={classes.error}>
                                            {touched.machine_centers &&
                                                errors.machine_centers &&
                                                t(errors.machine_centers)}
                                        </FormHelperText>
                                    </>
                                </Box>
                            </Box>
                        </div>
                        <div className={classes.footer}>
                            <Button
                                variant="text"
                                color="info"
                                className={classes.button}
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                {t("corporate_backoffice.cluster_view.cluster_creation_form.cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                className={classes.submitButton}
                                type="submit"
                                disabled={isSubmitting}
                            >
                                <CheckOutlinedIcon />
                                {t("corporate_backoffice.cluster_view.cluster_creation_form.confirm")}
                            </Button>
                            {cluster && CORPORATE_PERMISSIONS.corporationPermissions.canDelete && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="info"
                                        className={classes.deleteButton}
                                        disabled={isSubmitting}
                                        onClick={() => setDeleteClusterAction(true)}
                                    >
                                        <DeleteIcon />
                                        {t("corporate_backoffice.cluster_view.cluster_creation_form.delete")}
                                    </Button>
                                    <CheckUserPasswordModal
                                        open={deleteClusterAction}
                                        onConfirmPassword={handleDeleteCluster}
                                        onClose={() => {
                                            setDeleteClusterAction(false);
                                            handleClose();
                                        }}
                                        title={t(
                                            "corporate_backoffice.cluster_view.delete_cluster.confirm_delete_cluster_modal_title",
                                        )}
                                        question={t(
                                            "corporate_backoffice.cluster_view.delete_cluster.confirm_delete_cluster_modal_question",
                                            { cluster_name: cluster?.name },
                                        )}
                                        questionConfirmedButtonText={t(
                                            "corporate_backoffice.cluster_view.delete_cluster.confirm_delete_cluster_modal_title",
                                        )}
                                    />
                                </>
                            )}
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
